/**
 * Capacity Heatmap Widget Component
 * Displays work center utilization as a visual heatmap grid
 * Shows utilization percentages with color-coded cells
 */

import React, { useState, useMemo } from 'react';
import {
  Activity,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Zap,
  Info,
} from 'lucide-react';
import clsx from 'clsx';
import {
  HeatmapResponse,
  UtilizationResponse,
  getUtilizationColor,
  getUtilizationBarColor,
} from '../../hooks/useCapacityData';

export interface CapacityHeatmapWidgetProps {
  heatmap?: HeatmapResponse;
  utilization?: UtilizationResponse;
  loading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
  onSelectScenario?: () => void;
  scenarioId?: string;
}

// Format date for display
function formatDate(dateStr: string, format: 'short' | 'day' = 'short'): string {
  const date = new Date(dateStr);
  if (format === 'day') {
    return date.toLocaleDateString('en-GB', { weekday: 'short' }).substring(0, 2);
  }
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
}

// Format day of month
function formatDayOfMonth(dateStr: string): string {
  const date = new Date(dateStr);
  return date.getDate().toString();
}

// Check if weekend
function isWeekend(dateStr: string): boolean {
  const date = new Date(dateStr);
  const day = date.getDay();
  return day === 0 || day === 6;
}

export function CapacityHeatmapWidget({
  heatmap,
  utilization,
  loading,
  error,
  onRefresh,
  onSelectScenario,
  scenarioId,
}: CapacityHeatmapWidgetProps) {
  const [viewMode, setViewMode] = useState<'heatmap' | 'summary'>('summary');
  const [hoveredCell, setHoveredCell] = useState<{ wc: string; date: string } | null>(null);

  // Get all unique dates from heatmap data
  const allDates = useMemo(() => {
    if (!heatmap?.heatmap) return [];
    const dates = new Set<string>();
    heatmap.heatmap.forEach(wc => {
      Object.keys(wc.dates).forEach(date => dates.add(date));
    });
    return Array.from(dates).sort();
  }, [heatmap]);

  // Calculate overall stats
  const overallStats = useMemo(() => {
    if (!utilization?.work_centers) {
      return { avgUtilization: 0, overloaded: 0, underutilized: 0 };
    }

    const wcs = utilization.work_centers;
    const avgUtilization =
      wcs.length > 0
        ? wcs.reduce((sum, wc) => sum + wc.utilization_percent, 0) / wcs.length
        : 0;
    const overloaded = wcs.filter(wc => wc.utilization_percent >= 90).length;
    const underutilized = wcs.filter(wc => wc.utilization_percent < 50).length;

    return { avgUtilization, overloaded, underutilized };
  }, [utilization]);

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-6 w-40 bg-gray-200 rounded" />
            <div className="h-8 w-24 bg-gray-200 rounded" />
          </div>
          <div className="h-40 bg-gray-100 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!scenarioId) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#45bdd6]" />
            <span className="font-medium text-gray-700">Capacity Heatmap</span>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-gray-500">
          <Calendar className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-sm font-medium">No Scenario Selected</p>
          <p className="text-xs text-gray-400 mt-1 text-center">
            Select a planning scenario to view capacity utilization
          </p>
          {onSelectScenario && (
            <button
              onClick={onSelectScenario}
              className="mt-4 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3ba8bf] transition-colors text-sm"
            >
              Select Scenario
            </button>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#45bdd6]" />
            <span className="font-medium text-gray-700">Capacity Heatmap</span>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-red-500">
          <AlertTriangle className="w-12 h-12 text-red-300 mb-3" />
          <p className="text-sm font-medium">Failed to load data</p>
          <p className="text-xs text-gray-400 mt-1">{error.message}</p>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="mt-4 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-[#45bdd6]" />
          <span className="font-medium text-gray-700">Capacity Heatmap</span>
          {heatmap?.scenario_name && (
            <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full truncate max-w-[150px]">
              {heatmap.scenario_name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-200 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('summary')}
              className={clsx(
                'px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'summary'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              Summary
            </button>
            <button
              onClick={() => setViewMode('heatmap')}
              className={clsx(
                'px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'heatmap'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              Heatmap
            </button>
          </div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-2 rounded hover:bg-gray-200 text-gray-500 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {viewMode === 'summary' ? (
          /* Summary View - Utilization Bars */
          <div className="space-y-4">
            {/* Overall Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {overallStats.avgUtilization.toFixed(0)}%
                </div>
                <div className="text-xs text-gray-600">Avg. Utilization</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-600">
                  {overallStats.overloaded}
                </div>
                <div className="text-xs text-gray-600">Overloaded (≥90%)</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {overallStats.underutilized}
                </div>
                <div className="text-xs text-gray-600">Underutilized (&lt;50%)</div>
              </div>
            </div>

            {/* Work Center Bars */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Work Center Utilization
              </div>
              {utilization?.work_centers && utilization.work_centers.length > 0 ? (
                utilization.work_centers.map(wc => {
                  const barColor = getUtilizationBarColor(wc.utilization_percent);
                  const isOverloaded = wc.utilization_percent >= 100;

                  return (
                    <div key={wc.work_center_code} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-xs text-gray-500">
                            {wc.work_center_code}
                          </span>
                          <span className="text-sm text-gray-700 truncate">
                            {wc.work_center_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isOverloaded && (
                            <AlertTriangle className="w-3 h-3 text-red-500" />
                          )}
                          <span
                            className={clsx(
                              'text-sm font-medium',
                              isOverloaded ? 'text-red-600' : 'text-gray-700'
                            )}
                          >
                            {(wc.utilization_percent ?? 0).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={clsx(
                            'h-full rounded-full transition-all duration-300',
                            barColor
                          )}
                          style={{ width: `${Math.min(wc.utilization_percent, 100)}%` }}
                        />
                      </div>
                      {/* Tooltip on hover */}
                      <div className="hidden group-hover:flex justify-between text-xs text-gray-500 mt-1">
                        <span>{(wc.total_planned_hours ?? 0).toFixed(1)}h planned</span>
                        <span>{(wc.total_capacity_hours ?? 0).toFixed(1)}h capacity</span>
                        <span>{wc.operation_count ?? 0} ops</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Zap className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm">No utilization data available</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Heatmap View - Grid */
          <div className="space-y-4">
            {heatmap?.heatmap && heatmap.heatmap.length > 0 && allDates.length > 0 ? (
              <>
                {/* Legend */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <Info className="w-3 h-3" />
                    <span>Utilization %</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-gray-100 rounded" title="0%" />
                    <div className="w-4 h-4 bg-green-200 rounded" title="1-25%" />
                    <div className="w-4 h-4 bg-green-300 rounded" title="25-50%" />
                    <div className="w-4 h-4 bg-yellow-400 rounded" title="50-75%" />
                    <div className="w-4 h-4 bg-orange-400 rounded" title="75-90%" />
                    <div className="w-4 h-4 bg-red-400 rounded" title="90-100%" />
                    <div className="w-4 h-4 bg-red-500 rounded" title=">100%" />
                  </div>
                </div>

                {/* Heatmap Grid */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="text-left py-2 pr-2 sticky left-0 bg-white z-10">
                          Work Center
                        </th>
                        {allDates.map(date => (
                          <th
                            key={date}
                            className={clsx(
                              'text-center px-1 py-1 min-w-[32px]',
                              isWeekend(date) && 'bg-gray-50'
                            )}
                          >
                            <div className="text-gray-400">{formatDate(date, 'day')}</div>
                            <div className="font-medium">{formatDayOfMonth(date)}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {heatmap.heatmap.map(wc => (
                        <tr key={wc.work_center_code} className="group">
                          <td className="py-1 pr-2 sticky left-0 bg-white z-10">
                            <div className="truncate max-w-[120px]" title={wc.work_center_name}>
                              <span className="font-mono text-gray-500">
                                {wc.work_center_code}
                              </span>
                            </div>
                          </td>
                          {allDates.map(date => {
                            const cellData = wc.dates[date];
                            const utilPercent = cellData?.utilization_percent || 0;
                            const { bg, text } = getUtilizationColor(utilPercent);
                            const isHovered =
                              hoveredCell?.wc === wc.work_center_code &&
                              hoveredCell?.date === date;

                            return (
                              <td
                                key={date}
                                className={clsx(
                                  'text-center px-1 py-1',
                                  isWeekend(date) && 'bg-gray-50'
                                )}
                              >
                                <div
                                  className={clsx(
                                    'w-7 h-7 rounded flex items-center justify-center cursor-pointer transition-all',
                                    bg,
                                    text,
                                    isHovered && 'ring-2 ring-blue-500 ring-offset-1'
                                  )}
                                  onMouseEnter={() =>
                                    setHoveredCell({ wc: wc.work_center_code, date })
                                  }
                                  onMouseLeave={() => setHoveredCell(null)}
                                  title={`${wc.work_center_name}\n${formatDate(date)}\n${(utilPercent ?? 0).toFixed(0)}% (${cellData?.planned_hours?.toFixed(1) ?? 0}h / ${cellData?.capacity_hours?.toFixed(1) ?? 0}h)`}
                                >
                                  {(utilPercent ?? 0) > 0 && (
                                    <span className="text-[10px] font-medium">
                                      {(utilPercent ?? 0).toFixed(0)}
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Hovered Cell Info */}
                {hoveredCell && (
                  <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
                    {(() => {
                      const wc = heatmap.heatmap.find(
                        w => w.work_center_code === hoveredCell.wc
                      );
                      const cellData = wc?.dates[hoveredCell.date];
                      if (!wc || !cellData) return null;
                      return (
                        <div className="flex justify-between">
                          <span>
                            <strong>{wc.work_center_name}</strong> - {formatDate(hoveredCell.date)}
                          </span>
                          <span>
                            {(cellData.planned_hours ?? 0).toFixed(1)}h / {(cellData.capacity_hours ?? 0).toFixed(1)}h
                            = <strong>{(cellData.utilization_percent ?? 0).toFixed(0)}%</strong>
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-medium">No heatmap data available</p>
                <p className="text-xs text-gray-400 mt-1">
                  Run AI calculation to generate planned operations
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
        <div className="flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          <span>
            {utilization?.planning_days || 0} days • {utilization?.work_centers?.length || 0} work
            centers
          </span>
        </div>
        <span>Auto-refresh: 60s</span>
      </div>
    </div>
  );
}

export default CapacityHeatmapWidget;
